import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import client, { mensajeDeError } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import Card from '../../components/ui/Card';
import QrScanner from '../../components/QrScanner';
import { Entrada, MensajeError, MensajeExito } from '../../components/ui/Form';
import { aISO, sumarDias, fechaCompleta, horaLegible } from '../../utils/fechas';
import { TituloSeccion, BarraAcciones, SinDatos } from './comunes';

const Grupo = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space.md};
  padding: 0;
  overflow: hidden;
`;

const CabeceraClase = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.sand};

  h3 {
    font-size: 1.02rem;
    margin-right: auto;
  }
`;

const Cliente = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: 0.7rem ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  strong {
    font-weight: 600;
  }

  span {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-right: auto;
  }
`;

const Rango = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const PanelCheckin = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space.lg};

  h2 {
    font-size: 1.05rem;
    color: ${({ theme }) => theme.colors.sageDeep};
    margin-bottom: ${({ theme }) => theme.space.sm};
  }

  p {
    font-size: 0.88rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: ${({ theme }) => theme.space.md};
  }

  form {
    display: flex;
    gap: ${({ theme }) => theme.space.sm};
    flex-wrap: wrap;
  }
`;

function ReservasAdmin() {
  const [desde, setDesde] = useState(() => aISO(new Date()));
  const [hasta, setHasta] = useState(() => aISO(sumarDias(new Date(), 6)));
  const [reservas, setReservas] = useState(null);
  const [error, setError] = useState('');
  const [codigo, setCodigo] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const [checkinError, setCheckinError] = useState('');
  const [checkinExito, setCheckinExito] = useState('');

  const cargar = useCallback(() => {
    client
      .get('/reservas', { params: { desde, hasta } })
      .then(({ data }) => setReservas(data))
      .catch((err) => {
        setError(mensajeDeError(err));
        setReservas([]);
      });
  }, [desde, hasta]);

  useEffect(() => {
    setReservas(null);
    cargar();
  }, [cargar]);

  const marcar = async (reservaId, estado) => {
    setError('');
    try {
      await client.patch(`/reservas/${reservaId}/asistencia`, { estado });
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  };

  const registrandoRef = useRef(false);

  const hacerCheckin = useCallback(
    async (codigoTexto) => {
      if (!codigoTexto || registrandoRef.current) return;
      registrandoRef.current = true;
      setRegistrando(true);
      setCheckinError('');
      setCheckinExito('');
      try {
        const { data } = await client.post('/reservas/checkin', { token: codigoTexto });
        setCheckinExito(data.mensaje);
        setCodigo('');
        cargar();
      } catch (err) {
        setCheckinError(mensajeDeError(err));
      } finally {
        registrandoRef.current = false;
        setRegistrando(false);
      }
    },
    [cargar]
  );

  const registrarEntrada = (e) => {
    e.preventDefault();
    hacerCheckin(codigo.trim());
  };

  // Agrupa por clase (fecha + hora + nombre) para mostrar el listado de clientes por clase
  const grupos = (reservas || []).reduce((acc, r) => {
    const clave = `${r.clase_id}`;
    if (!acc[clave]) {
      acc[clave] = { clase: r, clientes: [] };
    }
    acc[clave].clientes.push(r);
    return acc;
  }, {});

  return (
    <>
      <TituloSeccion>Reservas</TituloSeccion>

      <PanelCheckin>
        <h2>Registrar entrada por código QR</h2>
        <p>Apunta la cámara al QR que el cliente muestra en su celular para registrar su entrada a la clase.</p>
        {checkinError && <MensajeError role="alert">{checkinError}</MensajeError>}
        {checkinExito && <MensajeExito role="status">{checkinExito}</MensajeExito>}
        <QrScanner onDetectado={hacerCheckin} deshabilitado={registrando} />
        <details style={{ marginTop: '0.75rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'inherit' }}>
            ¿Sin cámara disponible? Ingresa el código manualmente
          </summary>
          <form onSubmit={registrarEntrada} style={{ marginTop: '0.75rem' }}>
            <Entrada
              type="text"
              placeholder="Pega aquí el código escaneado"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              aria-label="Código QR escaneado"
              style={{ flex: 1, minWidth: 240 }}
            />
            <Button type="submit" disabled={registrando}>
              {registrando ? 'Registrando…' : 'Registrar entrada'}
            </Button>
          </form>
        </details>
      </PanelCheckin>

      <BarraAcciones>
        <Rango>
          <label htmlFor="desde" style={{ fontSize: '0.85rem' }}>
            Desde
          </label>
          <Entrada id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <label htmlFor="hasta" style={{ fontSize: '0.85rem' }}>
            Hasta
          </label>
          <Entrada id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </Rango>
      </BarraAcciones>
      {error && <MensajeError role="alert">{error}</MensajeError>}

      {!reservas ? (
        <Spinner />
      ) : Object.keys(grupos).length === 0 ? (
        <Card>
          <SinDatos>No hay reservas en este rango.</SinDatos>
        </Card>
      ) : (
        Object.values(grupos).map(({ clase, clientes }) => (
          <Grupo key={clase.clase_id}>
            <CabeceraClase>
              <h3>
                {clase.tipo_clase} · {fechaCompleta(clase.fecha)} · {horaLegible(clase.hora_inicio)}
              </h3>
              <Badge $tono="arena">{clase.instructor}</Badge>
              <Badge>{clientes.length} reservas</Badge>
            </CabeceraClase>
            {clientes.map((r) => (
              <Cliente key={r.id}>
                <strong>{r.cliente}</strong>
                <span>{r.telefono || r.email}</span>
                {r.estado === 'asistio' && <Badge $tono="exito">Asistió</Badge>}
                {r.estado === 'falta' && <Badge $tono="peligro">Faltó</Badge>}
                {r.estado === 'reservada' ? (
                  <>
                    <Button $variante="secundario" $chico onClick={() => marcar(r.id, 'asistio')}>
                      Asistió
                    </Button>
                    <Button $variante="peligro" $chico onClick={() => marcar(r.id, 'falta')}>
                      Faltó
                    </Button>
                  </>
                ) : (
                  <Button $variante="fantasma" $chico onClick={() => marcar(r.id, 'reservada')}>
                    Deshacer
                  </Button>
                )}
              </Cliente>
            ))}
          </Grupo>
        ))
      )}
    </>
  );
}

export default ReservasAdmin;
